import { getFragmentData } from 'gql'
import { ErrorFragmentFragmentDoc } from 'gql/graphql'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_YIELDBIRD_DEMAND_REPORT } from './api/mutation'

import type { Schema } from '../schema'
import type { UseUpdateYieldbirdDemandReport } from './types'
import type {
  UpdateYieldbirdDemandReportUrlMutation,
  UpdateYieldbirdDemandReportUrlMutationVariables,
} from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateYieldbirdDemandReport = (): UseUpdateYieldbirdDemandReport => {
  const { t } = useTranslation('features/performance')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isPending, mutateAsync } = useMutation<
    { input: UpdateYieldbirdDemandReportUrlMutationVariables['input'] },
    UpdateYieldbirdDemandReportUrlMutation
  >({
    mutation: UPDATE_YIELDBIRD_DEMAND_REPORT,
    onCompleted: async (data: UpdateYieldbirdDemandReportUrlMutation) => {
      const apiErrors = data.updateWorkspace?.errors

      if (apiErrors && apiErrors.length > 0) {
        setErrors(mapGraphQLErrors(getFragmentData(ErrorFragmentFragmentDoc, apiErrors)))
        enqueueSnackbar(t('form.error.update'), {
          variant: 'error',
        })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), {
          variant: 'success',
        })
      }
    },
    queriesToInvalidate: ['getYieldbirdDemandReport'],
  })

  const update = (data: Schema): void => {
    void mutateAsync({ input: toDTO(data) })
  }

  return {
    errors,
    loading: isPending,
    update,
  }
}
